// @styles
require('./aggregatorTinkManageEntitiesAccordion.scss');

// vendors
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// components
import { Accordion } from 'commonsComponents/accordion';
import { AccordionHeader } from 'commonsComponents/accordionHeader';
import { AccordionBody } from 'commonsComponents/accordionBody';
import { OKIconButton } from 'commonsComponents/OKIconButton';
import { AggregatorTinkStatus } from 'components/aggregator/tink/aggregatorTinkStatus/aggregatorTinkStatus';
import Toggle from 'components/commons/toggle/toggle';

// utilities
import {
  recordLink,
  EVENT_ACTIVATE,
  EVENT_DESACTIVATE,
  EVENT_REMOVE,
  PROC_AGGREGATOR,
  PROC_MANAGE_ENTITIES_AGGREGATOR,
  PROC_MANAGE_ACCOUNTS_AGGREGATOR,
} from 'utilities/tagging';
const { formatText } = require('core/i18n').i18n;
import { getLastNChars } from 'utilities/stringHelper';

// assets
import accountIcon from 'routes/aggregatorTink/assets/account-icon.svg';
import cardIcon from 'routes/aggregatorTink/assets/card-icon.svg';
import loanIcon from 'routes/aggregatorTink/assets/loan-icon.svg';
import wealthIcon from 'routes/aggregatorTink/assets/wealth-icon.svg';

// constants
import {
  UPDATED,
  UPDATING,
  SESSION_EXPIRED,
  TEMPORARY_ERROR,
  AUTHENTICATION_ERROR,
  AWAITING_SUPPLEMENTAL_INFORMATION,
  AWAITING_THIRD_PARTY_APP_AUTHENTICATION,
  AWAITING_MOBILE_BANKID_AUTHENTICATION,
  DELETED,
} from 'constants/aggregator/tinkStatuses.constants';
import {
  OK,
  BAD_CREDENTIALS,
  KO,
  SYNC,
  BAD_SECOND_FACTOR_AUTHENTICATION,
  PARTIAL_OK,
  BLOCKED,
  ROBOT_ERROR,
  REVIEW,
  PSD2_CONSENT_INVALID,
} from 'constants/aggregator/statuses';
import {
  ACCOUNTS,
  CARDS,
  LOANSANDMORTGAGES,
  DEPOSITS,
  INVESTMENTS,
} from 'constants/aggregator/products';
import { SHARES } from 'constants/aggregator/subtypes';

const ERROR_STATUSES = [
  // Tink
  SESSION_EXPIRED,
  TEMPORARY_ERROR,
  AUTHENTICATION_ERROR,
  AWAITING_SUPPLEMENTAL_INFORMATION,
  AWAITING_THIRD_PARTY_APP_AUTHENTICATION,
  AWAITING_MOBILE_BANKID_AUTHENTICATION,
  // Eurobits
  BAD_CREDENTIALS,
  KO,
  PARTIAL_OK,
  BLOCKED,
  ROBOT_ERROR,
  REVIEW,
  PSD2_CONSENT_INVALID,
];

class AggregatorTinkManageEntitiesAccordion extends React.PureComponent {
  constructor(props) {
    super(props);

    const productList = {};
    props.products.forEach(item => {
      if (item) productList[item.get('id')] = item.get('visible');
    });

    this.state = {
      productVisibility: productList,
      isExpanded: true,
    };
  }

  handleOnToggle = (productId, productType, checked) => {
    const { toggleActive, onHandleDirty, bankId } = this.props;

    this.setState(prevState => ({
      productVisibility: {
        ...prevState.productVisibility,
        [productId]: checked,
      },
    }));

    toggleActive(productId, productType, checked, bankId);
    onHandleDirty();
    recordLink(
      PROC_AGGREGATOR,
      PROC_MANAGE_ACCOUNTS_AGGREGATOR,
      checked ? EVENT_ACTIVATE : EVENT_DESACTIVATE
    );
  };

  handleOnRemove = bank => {
    const { onHandleDirty, removeEntity } = this.props;

    removeEntity(bank);
    onHandleDirty();
    recordLink(PROC_AGGREGATOR, PROC_MANAGE_ENTITIES_AGGREGATOR, EVENT_REMOVE);
  };

  renderAccount(product) {
    return (
      <div className="aggregator-accordion__body__item__alias__account">
        <img className={'aggregator-accordion__body__item__alias__icon icon'} src={accountIcon} />
        <div className="aggregator-accordion__body__item__alias__div">
          <div className="aggregator-accordion__body__item__alias__div__name">
            {product.get('alias')}
          </div>
          <div className="aggregator-accordion__body__item__alias__div__number">
            {getLastNChars(product.get('iban', '').toString())}
          </div>
        </div>
      </div>
    );
  }

  renderCard(product) {
    return (
      <div className="aggregator-accordion__body__item__alias__cards">
        <img className={'aggregator-accordion__body__item__alias__icon icon'} src={cardIcon} />
        <div className="aggregator-accordion__body__item__alias__div">
          <div className="aggregator-accordion__body__item__alias__div__name">
            {product.get('alias')}
          </div>
          <div className="aggregator-accordion__body__item__alias__div__number">
            {getLastNChars(product.get('number', '').toString())}
          </div>
        </div>
      </div>
    );
  }

  renderLoanOrMortgage(product) {
    return (
      <div className="aggregator-accordion__body__item__alias__loans">
        <img className={'aggregator-accordion__body__item__alias__icon icon'} src={loanIcon} />
        <div className="aggregator-accordion__body__item__alias__div">
          <div className="aggregator-accordion__body__item__alias__div__name">
            {product.get('alias')}
          </div>
          <div className="aggregator-accordion__body__item__alias__div__number">
            {getLastNChars(product.get('iban', '').toString())}
          </div>
        </div>
      </div>
    );
  }

  renderDeposit(product) {
    return (
      <div className="aggregator-accordion__body__item__alias__deposits">
        <img className={'aggregator-accordion__body__item__alias__icon icon'} src={wealthIcon} />
        <div className="aggregator-accordion__body__item__alias__div">
          <div className="aggregator-accordion__body__item__alias__div__name">
            {product.get('alias')}
          </div>
          <div className="aggregator-accordion__body__item__alias__div__number">
            {getLastNChars(product.get('account', '').toString())}
          </div>
        </div>
      </div>
    );
  }

  renderInvestment(product) {
    return (
      <div className="aggregator-accordion__body__item__alias__investments">
        <img className={'aggregator-accordion__body__item__alias__icon icon'} src={wealthIcon} />
        <div className="aggregator-accordion__body__item__alias__div">
          <div className="aggregator-accordion__body__item__alias__div__name">
            {product.get('alias')}
          </div>
          {product.get('subproduct') !== SHARES ? (
            <div className="aggregator-accordion__body__item__alias__div__number">
              {getLastNChars(product.get('account', product.get('number', '')).toString())}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  renderError = () => {
    const { status, psd2Status } = this.props;

    const hasStatusError = ERROR_STATUSES.includes(psd2Status) || ERROR_STATUSES.includes(status);

    return (
      hasStatusError && [
        <td key="1">
          <span className="aggregator-accordion__header__warning">
            <i className="aggregator-accordion__header__warning__icon icon icon-error" />
            {formatText('aggregatorManageEntities-credentialsError')}
          </span>
        </td>,
      ]
    );
  };

  renderProduct = product => {
    const funcMap = {
      [ACCOUNTS]: this.renderAccount,
      [CARDS]: this.renderCard,
      [LOANSANDMORTGAGES]: this.renderLoanOrMortgage,
      [DEPOSITS]: this.renderDeposit,
      [INVESTMENTS]: this.renderInvestment,
    };

    const item =
      product && funcMap[product.get('product')] ? funcMap[product.get('product')](product) : null;

    const visible = this.state.productVisibility[product.get('id')];

    return product ? (
      <div key={product.get('id')} className="aggregator-accordion__body__item row">
        <div className="aggregator-accordion__body__item__alias col-xs-8">{item}</div>
        <div className="aggregator-accordion__body__item__toggle col-xs-4">
          <Toggle
            checked={visible}
            onClick={() =>
              this.handleOnToggle(product.get('id'), product.get('subproduct'), !visible)
            }
          />
        </div>
      </div>
    ) : null;
  };

  renderBody = () => {
    const {
      products,
      status,
      bankName,
      bankCode,
      bankId,
      getSecondFactorAuth,
      psd2Status,
      bankLogo,
      bankTinkCredentials,
    } = this.props;

    const showStatusComponent = ![DELETED, OK, UPDATED].includes(status);
    const showPSD2StatusComponent = ![DELETED, OK, UPDATED].includes(psd2Status);
    const hasErrorsAndNoProducts =
      ERROR_STATUSES.includes(psd2Status) && ERROR_STATUSES.includes(status) && !products;

    return (
      <React.Fragment>
        <div className="aggregator-accordion__body">
          {products.map(p => p && this.renderProduct(p))}
        </div>

        {psd2Status
          ? showPSD2StatusComponent && (
              <AggregatorTinkStatus
                bankName={bankName}
                bankStatus={psd2Status}
                bankLogo={bankLogo}
                userBankId={bankId}
                bankCode={bankCode}
                settingsVariant={true}
                deprecated={psd2Status === BAD_SECOND_FACTOR_AUTHENTICATION}
                getSecondFactorAuth={getSecondFactorAuth}
                hasErrorsAndNoProducts={hasErrorsAndNoProducts}
                bankTinkCredentials={bankTinkCredentials}
              />
            )
          : showStatusComponent && (
              <AggregatorTinkStatus
                bankName={bankName}
                bankStatus={status}
                userBankId={bankId}
                bankLogo={bankLogo}
                settingsVariant={true}
                bankCode={bankCode}
                deprecated={status === BAD_SECOND_FACTOR_AUTHENTICATION}
                getSecondFactorAuth={getSecondFactorAuth}
                hasErrorsAndNoProducts={hasErrorsAndNoProducts}
                bankTinkCredentials={bankTinkCredentials}
              />
            )}
      </React.Fragment>
    );
  };

  render() {
    const { bankName, products, bankId, status, psd2Status } = this.props;
    const { isExpanded } = this.state;

    const disableButton =
      [UPDATING, SYNC].includes(psd2Status) || [UPDATING, SYNC].includes(status);

    const productsJS = !!products && products.toJS();
    const productsAmount = productsJS.filter(product => !!product).length;

    const aggAccordionClasses = classNames(
      'aggregator-manage-entities__content__products__item__box',
      {
        'aggregator-manage-entities__content__products__item__box--is-expanded': isExpanded,
      }
    );

    return (
      <div className={aggAccordionClasses}>
        <Accordion
          onToggle={() =>
            this.setState({
              isExpanded: isExpanded ? false : true,
            })
          }
          isExpanded={isExpanded}
        >
          <AccordionHeader name="AccordionHeader">
            <table className="aggregator-accordion__header">
              <td className="aggregator-accordion__header__name-container">
                <span className="aggregator-accordion__header__name">{bankName}</span>
                {<span className="aggregator-accordion__header__number">({productsAmount})</span>}
              </td>
              {this.renderError()}
              <td>
                <span className="aggregator-accordion__header__button-div">
                  <OKIconButton
                    icon="aggregator-accordion__header__icon icon icon-eliminar"
                    size="medium"
                    onClick={() => this.handleOnRemove(bankId)}
                    disabled={disableButton}
                  />
                </span>
              </td>
            </table>
          </AccordionHeader>
          <AccordionBody name="AccordionBody">{this.renderBody()}</AccordionBody>
        </Accordion>
      </div>
    );
  }
}

AggregatorTinkManageEntitiesAccordion.propTypes = {
  bankName: PropTypes.string,
  bankCode: PropTypes.string,
  bankId: PropTypes.string,
  products: PropTypes.object,
  removeEntity: PropTypes.func,
  toggleActive: PropTypes.func,
  onHandleDirty: PropTypes.func,
  status: PropTypes.string,
  psd2Status: PropTypes.string,
  bankLogo: PropTypes.string,
  getSecondFactorAuth: PropTypes.func,
  isAggregatorTink: PropTypes.bool,
  bankTinkCredentials: PropTypes.bool,
};

export { AggregatorTinkManageEntitiesAccordion };
